import { ChangeDetectionStrategy, Component, Input, ChangeDetectorRef } from '@angular/core';
import { FormGroup, FormArray } from '@angular/forms';
import { NormalizedSection, NormalizedUIField } from '../../store/config.models';
import { Store } from '@ngrx/store';
import { addArraySectionItem, removeArraySectionItem } from '../../store/actions';
import { DiagnosticsInputs, getDisabledByIdentifier, selectDiagnosticsInputs, selectDiagnosticsVisible } from '../../store/selectors';
import { map, Observable, of } from 'rxjs';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { RowItemType, SectionType, UIElementType } from '../../store/models';
import { DialogActionsFieldId } from '../../store/configs/common.models';
import { FieldType } from '../../store/models/field-models';

@Component({
    selector: 'app-dynamic-section',
    templateUrl: './section.component.html',
    styleUrls: ['./section.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: [
        trigger('expandContent', [
            state(
                'collapsed',
                style({
                    height: '0px',
                    padding: '0px',
                    visibility: 'hidden',
                    overflow: 'hidden',
                }),
            ),
            state(
                'expanded',
                style({
                    height: '*',
                    padding: '16px',
                    visibility: 'visible',
                    overflow: 'visible',
                }),
            ),
            transition('expanded => collapsed', [
                animate('225ms cubic-bezier(0.4, 0, 0.2, 1)'),
            ]),
            transition('collapsed => expanded', [
                animate('225ms cubic-bezier(0.4, 0, 0.2, 1)'),
            ]),
        ]),
    ],
})
export class DynamicSectionComponent {
    @Input() section: NormalizedSection;
    @Input() form: FormGroup;
    @Input() isDialogActions = false;
    @Input() isNestedSection = false;
    @Input() isReadOnly = false;

    errorCount: number = 0;
    RowItemType = RowItemType;
    UIElementType = UIElementType;

    diagnosticsVisible$: Observable<boolean>;
    diagnosticsInputs$: Observable<DiagnosticsInputs | null>;

    constructor(private store: Store, private cdr: ChangeDetectorRef) {
        this.diagnosticsVisible$ = this.store.select(selectDiagnosticsVisible);
        this.diagnosticsInputs$ = this.store.select(selectDiagnosticsInputs);
    }

    // Get FormControl for a specific field ID
    getFieldControl(fieldId: string) {
        return this.form?.get(fieldId);
    }

    // Get field definition from section
    getField(fieldId: string) {
        return this.section?.fields[fieldId];
    }

    getActionField(fieldId: string) {
        const field = this.section?.fields[fieldId];

        if (!field || field.visible === false) {
            return false;
        }

        return field;
    }

    getDisabledSelector(fieldId: string): Observable<boolean> {
        const field = this.getField(fieldId);
        if (!field || field.type !== FieldType.UI) {
            return of(true);
        }

        const isPermissionDisabled = this.isReadOnly && fieldId !== DialogActionsFieldId.CancelButton;

        if (field.disabledSelector) {
            return this.store
                .select<boolean>(getDisabledByIdentifier(field.disabledSelector))
                .pipe(map((isDisabled) => isDisabled || !!field.disabled || isPermissionDisabled));
        }

        return of(!!field.disabled || isPermissionDisabled);
    }

    // Get array control for array sections
    getArrayControl(sectionId: string): FormArray | null {
        return this.form?.get(sectionId) as FormArray;
    }

    // Check if this section has errors
    hasErrors(): boolean {
        this.errorCount = 0;
        if (!this.form) return false;

        // Count invalid controls
        Object.keys(this.form.controls).forEach((key) => {
            const control = this.form.get(key);
            if (control && control.invalid && (control.touched || control.dirty)) {
                this.errorCount++;
            }
        });

        return this.form.invalid;
    }

    // Helper method to check if section is array type
    isArraySection(): boolean {
        return this.section?.type === SectionType.Array;
    }

    isMaxItemsReached(): boolean {
        return this.section?.maxItems > 0 && this.section.rows?.length >= this.section.maxItems;
    }

    addArrayItem(): void {
        if (this.isReadOnly || !this.isArraySection() || !this.form || this.isMaxItemsReached()) return;

        this.store.dispatch(addArraySectionItem({ fieldId: this.section.id }));
    }

    removeArrayItem(index: number): void {
        if (this.isReadOnly || !this.isArraySection() || !this.form) return;

        this.store.dispatch(removeArraySectionItem({ fieldId: this.section.id, index }));
    }

    isActionsSection(): boolean {
        return this.section?.type === SectionType.Actions || this.isDialogActions;
    }

    handleFieldEvent(field: NormalizedUIField, eventName: string, event: Event) {
        if (this.isReadOnly && field.id !== DialogActionsFieldId.CancelButton) {
            return;
        }

        if (field.events && field.events[eventName]) {
            const actions = field.events[eventName](event);
            if (Array.isArray(actions)) {
                actions.forEach((action) => {
                    this.store.dispatch(action);
                });
            }
        }
    }

    handlePanelHeaderClick(event: MouseEvent): void {
        // For nested sections, we need to completely handle the event
        if (this.isNestedSection) {
            // Prevent the default accordion behavior
            event.preventDefault();
            event.stopPropagation();

            // Manually toggle the expanded state
            this.section.expanded = !this.section.expanded;

            // Force change detection to update the view
            this.cdr.markForCheck();

            // This is critical to prevent event bubbling up to parent expansion panels
            event.stopImmediatePropagation();
        }
    }

    // New method to manually toggle expansion
    toggleExpansion(event: MouseEvent): void {
        if (this.isNestedSection) {
            this.section.expanded = !this.section.expanded;
            this.cdr.markForCheck();

            // Stop propagation
            event.preventDefault();
            event.stopPropagation();
        }
    }
}
